<template>

<div class="main list-view">

	<div v-if="showDetail" class="sub-header detail-sub-header">
		<div class="detail-title">{{ listDetail.department }} <i class="fa fa-chevron-right" /> {{ listDetail.description }}</div>
		<div></div>
		<pagination-detail :list="list" :index="listIndex" @updatePage="getItem" />
	</div>
	<div v-else class="sub-header">
		<div class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div v-if="showDetail" class="filters">
		<button :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save</button>
		<button class="button neutral" @click="backToList()">Back to List</button>
		<div v-if="returnRoute" class="divider"></div>
		<router-link v-if="returnRoute" :to="returnRoute" class="extra-menu-item">Return to previous program</router-link>
	</div>
	<div v-else class="filters">
		<label>Department</label>
		<select v-model="filters.department_id" @change="getList(1)">
			<option value="">Select...</option>
			<option v-for="(department, index) in departments" :value="department.department_id">{{ department.description }}</option>
		</select>
		<ul class="checkbox-group">
			<li>
				<span :class="['pointer', 'far', filters.no_profit_info ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('no_profit_info')"></span>
				<label>Missing Profit %</label>
			</li>
		</ul>
		<div :class="['button',  hasFilters ? 'neutral' : 'disabled' ]" @click="clearFilters()">Clear Filters</div>
		<div class="divider"></div>
		<div :class="['extra-menu-item', { disabled: gettingExport }]" @click="doExport()">Export CSV</div>
		<div v-if="getDevEnvironment" class="extra-menu-item" @click="getList()">Reload Report</div>
	</div>

	<div v-if="showDetail" class="detail two-one-columns">

		<div>

			<div class="two-columns settings-columns align-items-baseline">
				<div class="settings-group">
					<h3>Profit Model</h3>
					<label>How is the customer's price calculated?</label>
					<div>
				        <label class="radio first">
							<input type="radio" v-model="listDetail.use_profit_percent" value="1">
							<span><i class="fas fa-check-circle"></i>Profit Percent</span>
						</label>
				        <label class="radio last">
							<input type="radio" v-model="listDetail.use_profit_percent" value="0">
							<span><i class="fas fa-check-circle"></i>Markup Amount</span>
						</label>
					</div>
				</div>
			</div>

			<div v-if="listDetail.use_profit_percent == 1" class="two-columns settings-columns align-items-baseline">
				<div class="settings-group">
					<h3>Default Profit Margin</h3>
					<number-input :decimals='1' v-model="listDetail.profit_percent" :key="'profit_percent_' + listDetail.use_profit_percent + listDetail.profit_percent" />
				</div>
				<div class="instruction">Customer Price =<br>Cost / ( 1 - ( Default Profit Margin / 100 ) )</div>
				<div class="settings-group">
					<h3>Low Cost Tier (optional)</h3>
					<label>If dealer cost is under...</label>
					<number-input :decimals='2' v-model="listDetail.low_profit_cost" :key="'low_profit_cost' + listDetail.use_profit_percent + listDetail.low_profit_cost" />
					<label>...then apply this profit margin</label>
					<number-input :decimals='1' v-model="listDetail.low_profit_percent" :key="'low_profit_percent' + listDetail.use_profit_percent + listDetail.low_profit_percent" />
				</div>
				<div class="instruction">Customer Price =<br>Cost / ( 1 - ( Low Profit Margin / 100 ) )</div>
				<div class="settings-group">
					<h3>Middle Cost Tier (optional)</h3>
					<label>If dealer cost is under...</label>
					<number-input :decimals='2' v-model="listDetail.middle_profit_cost" :key="'middle_profit_cost' + listDetail.use_profit_percent + listDetail.middle_profit_cost" />
					<label>...then apply this profit margin</label>
					<number-input :decimals='1' v-model="listDetail.middle_profit_percent" :key="'middle_profit_percent' + listDetail.use_profit_percent + listDetail.middle_profit_percent" />
				</div>
				<div class="instruction">Customer Price =<br>Cost / ( 1 - ( Middle Profit Margin / 100 ) )</div>
			</div>

			<div v-else class="two-columns settings-columns align-items-baseline">
				<div class="settings-group">
					<h3>Default Markup Amount</h3>
					<number-input :decimals='2' v-model="listDetail.markup_amount" :key="'markup_amount' + listDetail.use_profit_percent + listDetail.markup_amount" />
				</div>
				<div class="instruction">Customer Price =<br>Cost + Markup Amount</div>
				<div class="settings-group">
					<h3>Low Cost Tier (optional)</h3>
					<label>If dealer cost is under...</label>
					<number-input :decimals='2' v-model="listDetail.low_markup_cost" :key="'low_markup_cost' + listDetail.use_profit_percent + listDetail.low_markup_cost" />
					<label>...then apply this markup amount</label>
					<number-input :decimals='2' v-model="listDetail.low_markup_amount" :key="'low_markup_amount' + listDetail.use_profit_percent + listDetail.low_markup_amount" />
				</div>
				<div class="instruction">Customer Price =<br>Cost + Low Markup Amount</div>
				<div class="settings-group">
					<h3>Middle Cost Tier (optional)</h3>
					<label>If dealer cost is under...</label>
					<number-input :decimals='2' v-model="listDetail.middle_markup_cost" :key="'middle_markup_cost' + listDetail.use_profit_percent + listDetail.middle_markup_cost" />
					<label>...then apply this markup amount</label>
					<number-input :decimals='2' v-model="listDetail.middle_markup_amount" :key="'middle_markup_amount' + listDetail.use_profit_percent + listDetail.middle_markup_amount"/>
				</div>
				<div class="instruction">Customer Price =<br>Cost + Middle Markup Amount</div>
			</div>

		</div>

		<div>
			<h3>Rounding</h3>
			<label>Round price up to nearest dollar</label>
			<div>
		        <label class="radio first">
					<input type="radio" v-model="listDetail.round_up" value="1">
					<span><i class="fas fa-check-circle"></i>Yes</span>
				</label>
		        <label class="radio last">
					<input type="radio" v-model="listDetail.round_up" value="0">
					<span><i class="fas fa-check-circle"></i>No</span>
				</label>
			</div>
			<h3>Department</h3>
			<label>Use these settings for all categories in the {{ listDetail.department }} department?</label>
			<div>
		        <label class="radio first">
					<input type="radio" v-model="listDetail.set_for_department" value="1">
					<span><i class="fas fa-check-circle"></i>Yes</span>
				</label>
		        <label class="radio last">
					<input type="radio" v-model="listDetail.set_for_department" value="0">
					<span><i class="fas fa-check-circle"></i>No</span>
				</label>
			</div>
			<label><b>WARNING:</b> clicking "Yes" and saving will update every category in the {{ listDetail.department }} department. This cannot be undone.</label>

			<div style="margin: 2em 0 0 0; padding: 0.5em 0 0 1em; border-top: 1px solid #ccc; border-left: 1px solid #ccc;">
				<h3>Test</h3>
				<label>If your item cost is...</label>
				<div>
					<number-input :decimals='2' v-model="testCost" style="display: inline-block;"/> <i class="fa fa-calculator" style="font-size: 1.25em; margin: 0 0 0 0.5em; cursor: pointer;" @click="calculatePrice()"/>
				</div>
				<label>...then your customer's price will be...</label>
				<number-input :disabled="true" :decimals='2' v-model="testPrice" :key="testPrice" />
			</div>

		</div>

	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

	    <table v-else class="data-list" cellpadding="0" cellspacing="0">
			<thead>
				<tr>
					<th class="list left">Department</th>
					<th class="list left">Category</th>
					<th class="list center">Use %</th>
					<th class="list right">Profit %</th>
					<th class="list right">Tier 1 %</th>
					<th class="list right">Tier 2 %</th>
					<th class="list right">Markup $</th>
					<th class="list right">Tier 1 $</th>
					<th class="list right">Tier 2 $</th>
					<th class="list center">Round Up</th>
				</tr>
			</thead>
			<tr v-for="(item, index) in list" class="clickable-list-item" @click="getItem(item)">
				<td class="list left">{{ item.department }}</td>
				<td class="list left">{{ item.description }}</td>
				<td class="list center"><i v-if="item.use_profit_percent == 1" class="fas fa-check"></i></td>
				<td class="list right">{{ item.profit_percent }}</td>
				<td class="list right">{{ item.low_profit_percent }}</td>
				<td class="list right">{{ item.middle_profit_percent }}</td>
				<td class="list right">{{ item.markup_amount }}</td>
				<td class="list right">{{ item.low_markup_amount }}</td>
				<td class="list right">{{ item.middle_markup_amount }}</td>
				<td class="list center"><i v-if="item.round_up == 1" class="fas fa-check"></i></td>
			</tr>
		</table>

	</div>

    <message-modal v-if="message" :message="message" :loading="loading || saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

	<iframe id="download" src="" style="display: none;"></iframe>

	<help-popup :helpLink="{169:'Profit Margins'}" :tours="false"></help-popup>

</div>

</template>

<script>
import Loading from '@/components/Loading.vue'
import NumberInput from '@/components/NumberInput.vue'
import Message from '@/components/Message.vue'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'profit-margins',
	props: ['category_id'],
    components: {
        'loading': Loading,
		'number-input': NumberInput,
        'message': Message,
		'help-popup': HelpPopup
    },
	data() {
		return {
            loading: false,
			filters: {},
			filterMessage: '',
			departments: [],
			list: [],
			listIndex: 0,
			paging: {},
			showDetail: false,
			listDetail: {},
			gettingExport: false,
			exportMessage: '',
			saving: false,
			message: '',
			messageTimeout: 0,
			messageStatus: '',
			testCost: '',
			testPrice: '',
			returnRoute: ''
		}
	},
	created() {
		this.returnRoute = this.$route.query.return
		this.getDepartments()
		if (this.category_id) {
			this.getItem({category_id: this.category_id})
		}
		this.clearFilters()
	},
	computed: {
		canSave() {
			return true
		},
		hasFilters() {
			return this.filters.search_term || this.filters.department_id || this.filters.no_profit_info
		}
	},
	methods: {
		clearFilters() {
			// if filters were initilaly set but currently empty, don't reload list
			if (this.filters.hasOwnProperty('search_term') && !this.hasFilters) {
				return
			}
			this.filters = {
				page: 1,
				search_term: '',
				department_id: '',
				no_profit_info: 0,
				internal_use_only: 0,
				inactive: 0,
				do_export: 0
			}
			if (!this.category_id) {
				this.getList()
			}
		},
		getDepartments() {
			this.apiCall({ destination: 'get_list_departments' }).then(obj => {
				this.departments = obj.departments
			}).catch(err => {
				console.log(err)
			})
		},
		getList(page) {
			if (this.loading) { return }
			if (page) {
				this.filters.page = page
			}
			this.messageStatus = ''
			this.messageTimeout = 0
			if (this.gettingExport) {
				this.saving = true
				this.message = 'Generating CSV for ' + this.list.length + ' items...'
			} else {
				this.loading = true
				this.message = 'Getting list...'
			}
			this.apiCall({ destination: 'get_list_category_profit', data: this.filters }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				if (this.filters.do_export) {
					this.saving = false
					this.filters.do_export = 0
					this.message = "Downloading export file..."
					// accommodate live server filecache latency
					setTimeout(() => {
						this.messageTimeout = 1500
						this.message = "Export file<br>" + obj.file_name + "<br>downloaded!"
						document.getElementById("download").src = obj.download_url
						this.gettingExport = false
					}, 1500)
				} else {
					this.list = obj.list
					if (obj.paging) {
						this.paging = obj.paging
					}
					this.message = ''
				}
			}).catch(err => {
				console.log(err)
			})
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.filters.page = 1
			this.getList()
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		getItem(item) {
			if (this.loading) { return }
			this.loading = true
			this.messageStatus = ''
			this.messageTimeout = 0
			this.message = "Loading..."
			this.apiCall({ destination: 'get_item_category_profit', data: { category_id: item.category_id } }).then(obj => {
				this.loading = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.listDetail = obj.category_profit
					this.showDetail = true
					this.message = ''
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveItem() {
			if (this.saving) { return }
			this.saving = true
			this.messageStatus = ''
			this.messageTimeout = 0
			this.message = "Saving..."
			this.apiCall({ destination: 'save_item_category_profit', data: this.listDetail }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Category profit margin saved!'
				} else {
					this.message = obj.message
				}
				this.messageTimeout = 3000
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.listDetail = {}
			this.showDetail = false
			this.testCost = ''
			this.testPrice = ''
			this.getList()
		},
		doExport() {
			if (this.gettingExport) { return false }
			this.gettingExport = true
			this.filters.do_export = true
			this.getList()
		},
		calculatePrice() {
			if (!this.testCost) { return }
			let profitPercent = 0
			let markupAmount = 0
			if (this.listDetail.use_profit_percent == 1) {
				profitPercent = Number(this.listDetail.profit_percent)
				if (this.listDetail.low_profit_percent && this.listDetail.low_profit_cost && Number(this.testCost) <= Number(this.listDetail.low_profit_cost)) {
					profitPercent = Number(this.listDetail.low_profit_percent)
				} else if (this.listDetail.middle_profit_percent && this.listDetail.middle_profit_cost && Number(this.testCost) <= Number(this.listDetail.middle_profit_cost)) {
					profitPercent = Number(this.listDetail.middle_profit_percent)
				}
			} else {
				markupAmount = Number(this.listDetail.markup_amount)
				if (this.listDetail.low_markup_cost && this.listDetail.low_markup_amount && Number(this.testCost) <= Number(this.listDetail.low_markup_cost)) {
					markupAmount = Number(this.listDetail.low_markup_amount)
				} else if (this.listDetail.middle_markup_cost && this.listDetail.middle_markup_amount && Number(this.testCost) <= Number(this.listDetail.middle_markup_cost)) {
					markupAmount = Number(this.listDetail.middle_markup_amount)
				}
			}
			let calculatedPrice = 0
			if (profitPercent > 0) {
				calculatedPrice = Number(this.testCost) / ( 1 - ( profitPercent / 100 ))
			} else {
				calculatedPrice = Number(this.testCost) + markupAmount
			}
			if (this.listDetail.round_up == 1) {
				calculatedPrice = Math.ceil(calculatedPrice);
			}
			this.testPrice = calculatedPrice.toFixed(2)
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
